/**
 * SnapshotService for loading versioned JSON snapshots from CDN
 */

export interface SnapshotManifest {
    gallery: string;
    settings: string;
}

export interface GallerySnapshot {
    version: string;
    generatedAt: string;
    items: Array<{
        id: string;
        title: string;
        description?: string;
        order: number;
        thumbnail_url: string;
        full_image_url: string;
        created_at: string;
        updated_at: string;
    }>;
}

export interface SettingsSnapshot {
    version: string;
    generatedAt: string;
    settings: {
        about_content: string;
        etsy_button_image_url?: string;
        instagram_button_image_url?: string;
        logo_override_url?: string;
        use_admin_gallery: string;
        use_admin_about: string;
    };
}

class SnapshotService {
    private baseUrl: string;

    constructor() {
        // Use the current domain for snapshot URLs
        this.baseUrl = window.location.origin;
    }

    /**
     * Fetch the manifest to get current snapshot versions
     */
    async fetchManifest(): Promise<SnapshotManifest> {
        try {
            console.log('Fetching manifest from:', `${this.baseUrl}/data/manifest.json`);
            const response = await fetch(`${this.baseUrl}/data/manifest.json`, {
                cache: 'no-store', // Always fetch fresh manifest
            });

            console.log('Manifest response status:', response.status);
            console.log('Manifest response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                if (response.status === 404) {
                    // Manifest doesn't exist yet, return empty manifest
                    console.log('Manifest not found (404), returning empty manifest');
                    return { gallery: '', settings: '' };
                }
                throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type');
            console.log('Manifest content-type:', contentType);
            
            if (!contentType || !contentType.includes('application/json')) {
                // Response is not JSON (likely HTML fallback), return empty manifest
                console.log('Manifest response is not JSON, returning empty manifest');
                // Log first 200 characters of response for debugging
                const text = await response.text();
                console.log('Manifest response preview:', text.substring(0, 200));
                return { gallery: '', settings: '' };
            }

            const manifest = await response.json();
            console.log('Manifest loaded successfully:', manifest);
            return manifest;
        } catch (error) {
            console.log('Error fetching manifest, returning empty manifest:', error);
            return { gallery: '', settings: '' };
        }
    }

    /**
     * Fetch gallery snapshot by version
     */
    async fetchGallerySnapshot(version: string): Promise<GallerySnapshot> {
        const url = `${this.baseUrl}/data/gallery.v${version}.json`;
        console.log('Fetching gallery snapshot from:', url);
        
        const response = await fetch(url, {
            cache: 'force-cache', // Use cached version if available
        });

        console.log('Gallery snapshot response status:', response.status);
        console.log('Gallery snapshot response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
            throw new Error(`Failed to fetch gallery snapshot: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.log('Gallery snapshot response is not JSON, content-type:', contentType);
            // Log first 200 characters of response for debugging
            const text = await response.text();
            console.log('Gallery snapshot response preview:', text.substring(0, 200));
            throw new Error('Gallery snapshot response is not JSON');
        }

        return response.json();
    }

    /**
     * Fetch settings snapshot by version
     */
    async fetchSettingsSnapshot(version: string): Promise<SettingsSnapshot> {
        const url = `${this.baseUrl}/data/settings.v${version}.json`;
        console.log('Fetching settings snapshot from:', url);
        
        const response = await fetch(url, {
            cache: 'force-cache', // Use cached version if available
        });

        console.log('Settings snapshot response status:', response.status);
        console.log('Settings snapshot response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
            throw new Error(`Failed to fetch settings snapshot: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.log('Settings snapshot response is not JSON, content-type:', contentType);
            // Log first 200 characters of response for debugging
            const text = await response.text();
            console.log('Settings snapshot response preview:', text.substring(0, 200));
            throw new Error('Settings snapshot response is not JSON');
        }

        return response.json();
    }

    /**
     * Load gallery data from snapshots with fallback to API
     */
    async loadGalleryData(apiFallback: () => Promise<any>): Promise<any> {
        try {
            // Try to load from snapshots first
            const manifest = await this.fetchManifest();
            const gallerySnapshot = await this.fetchGallerySnapshot(manifest.gallery);
            
            console.log('Loaded gallery data from snapshot:', gallerySnapshot.version);
            return { items: gallerySnapshot.items };
        } catch (error) {
            console.warn('Failed to load gallery from snapshot, falling back to API:', error);
            return apiFallback();
        }
    }

    /**
     * Load settings data from snapshots with fallback to API
     */
    async loadSettingsData(apiFallback: () => Promise<any>): Promise<any> {
        try {
            // Try to load from snapshots first
            const manifest = await this.fetchManifest();
            const settingsSnapshot = await this.fetchSettingsSnapshot(manifest.settings);
            
            console.log('Loaded settings data from snapshot:', settingsSnapshot.version);
            
            // Convert snapshot format to expected API format
            const settings = Object.entries(settingsSnapshot.settings).map(([key, value]) => ({
                key,
                value: value || '',
            }));

            return { settings };
        } catch (error) {
            console.warn('Failed to load settings from snapshot, falling back to API:', error);
            return apiFallback();
        }
    }

    /**
     * Check if snapshots are available (for feature detection)
     */
    async isAvailable(): Promise<boolean> {
        try {
            await this.fetchManifest();
            return true;
        } catch (error) {
            return false;
        }
    }
}

// Export singleton instance
export const snapshotService = new SnapshotService();
